/**
 * Central utility for property type mapping and formatting
 * Source of truth: NewListingPropertyTypeQuestion component
 */

export interface PropertyTypeOption {
    value: string;
    label: string;
}

export interface TypeOfPlaceOption {
    value: string;
    label: string;
}

// Property type options from NewListingPropertyTypeQuestion (source of truth)
export const PROPERTY_TYPE_OPTIONS: PropertyTypeOption[] = [
    { value: "house", label: "House" },
    { value: "apartment", label: "Apartment" },
    { value: "townhouse", label: "Townhouse" },
    { value: "cabin", label: "Cabin" },
    { value: "guest", label: "Guest house" },
    { value: "studio", label: "Studio" },
    { value: "chalet", label: "Chalet" },
    { value: "hotel", label: "Hotel" },
    { value: "serviced-apartment", label: "Serviced apartment" },
    { value: "resort", label: "Resort" },
    { value: "bed-and-breakfast", label: "Bed & Breakfast" },
    { value: "farm-stay", label: "Farm stay" },
    { value: "hostel", label: "Hostel" },
    { value: "motel", label: "Motel" },
    { value: "beach-house", label: "Beach house" },
    { value: "tree-house", label: "Tree house" },
    { value: "condo", label: "Condo" },
    { value: "cottage", label: "Cottage" },
    { value: "villa", label: "Villa" },
    { value: "lodge", label: "Lodge" },
    { value: "estate", label: "Estate" },
    { value: "tiny-home", label: "Tiny home" },
    { value: "yurt", label: "Yurt" },
    { value: "glamping", label: "Glamping" },
    { value: "guest-suite", label: "Guest suite" },
    { value: "penthouse", label: "Penthouse" },
    { value: "campsite", label: "Campsite" },
    { value: "station", label: "Station" },
    { value: "dome", label: "Dome" },
    { value: "boutique-hotel", label: "Boutique hotel" },
    { value: "barn", label: "Barn" },
    { value: "shed", label: "Shed" },
    { value: "shack", label: "Shack" },
    { value: "caravan", label: "Caravan" },
    { value: "wagon", label: "Wagon" },
    { value: "boat", label: "Boat" },
    { value: "yacht", label: "Yacht" },
    { value: "castle", label: "Castle" },
    { value: "island", label: "Island" },
    { value: "lake-house", label: "Lake house" },
    { value: "tower", label: "Tower" },
    { value: "aparthotel", label: "Aparthotel" },
    { value: "nature-lodge", label: "Nature lodge" },
    { value: "houseboat", label: "Houseboat" },
    { value: "lighthouse", label: "Lighthouse" },
    { value: "bungalow", label: "Bungalow" },
    { value: "hut", label: "Hut" },
    { value: "loft", label: "Loft" },
    { value: "train", label: "Train" },
    { value: "teepee", label: "Teepee" },
    { value: "bus", label: "Bus" },
    { value: "suite", label: "Suite" },
    { value: "cave", label: "Cave" },
    { value: "chatea", label: "Chateau" },
    { value: "riad", label: "Riad" },
    { value: "rv", label: "RV" },
    { value: "windmill", label: "Windmill" },
    { value: "sheperds-hut", label: "Shepherds hut" },
    { value: "tram", label: "Tram" },
    { value: "igloo", label: "Igloo" },
    { value: "church", label: "Church" },
    { value: "eco-house", label: "Eco house" },
    { value: "bunker", label: "Bunker" },
];

// Type of place options from NewListingPropertyTypeQuestion (source of truth)
export const TYPE_OF_PLACE_OPTIONS: TypeOfPlaceOption[] = [
    { value: "entire-place", label: "Entire place" },
    { value: "private-room", label: "Private room" },
    { value: "hotel-room", label: "Hotel room" },
    { value: "shared-room", label: "Shared room" },
];

// Create a lookup map for efficient property type formatting
const PROPERTY_TYPE_MAP = new Map<string, string>();
PROPERTY_TYPE_OPTIONS.forEach(option => {
    PROPERTY_TYPE_MAP.set(option.value, option.label);
});

/**
 * Formats a property type value to its display label
 * @param propertyType - The property type value (e.g., "guest", "guest-house")
 * @returns The formatted display label (e.g., "Guest house")
 */
export const formatPropertyType = (propertyType: string): string => {
    if (!propertyType) return "";
    
    // First try exact match
    const exactMatch = PROPERTY_TYPE_MAP.get(propertyType);
    if (exactMatch) return exactMatch;
    
    // Handle common variations (e.g., "guest-house" -> "guest")
    const normalizedType = propertyType.toLowerCase().replace(/-/g, "");
    for (const [key, value] of PROPERTY_TYPE_MAP.entries()) {
        if (key.replace(/-/g, "") === normalizedType) {
            return value;
        }
    }
    
    // Fallback: capitalize first letter and replace hyphens with spaces
    return propertyType
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

/**
 * Gets all property type options for dropdowns/selects
 * @returns Array of property type options
 */
export const getPropertyTypeOptions = (): PropertyTypeOption[] => {
    return [...PROPERTY_TYPE_OPTIONS];
};

/**
 * Gets all type of place options for dropdowns/selects
 * @returns Array of type of place options
 */
export const getTypeOfPlaceOptions = (): TypeOfPlaceOption[] => {
    return [...TYPE_OF_PLACE_OPTIONS];
};

/**
 * Finds a property type option by value
 * @param value - The property type value to find
 * @returns The property type option or undefined if not found
 */
export const findPropertyTypeOption = (value: string): PropertyTypeOption | undefined => {
    return PROPERTY_TYPE_OPTIONS.find(option => option.value === value);
};

/**
 * Finds a type of place option by value
 * @param value - The type of place value to find
 * @returns The type of place option or undefined if not found
 */
export const findTypeOfPlaceOption = (value: string): TypeOfPlaceOption | undefined => {
    return TYPE_OF_PLACE_OPTIONS.find(option => option.value === value);
};
